import { useCallback, useRef } from "react";
import { cn } from "@/lib/utils";

type InputProps = {
  className?: string;
  type?: string;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  align?: "start" | "end";
  color?: "destructive";
  inputMode?: "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url";
};

export const OrderFormInput = ({
  className,
  type,
  suffix,
  disabled,
  prefix,
  placeholder,
  value,
  align = "start",
  onChange = () => {},
  onFocus,
  onBlur,
  color,
  inputMode,
}: InputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const onAffixClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div
      className={cn(
        "rounded-md outline-none border transition-all duration-200",
        "flex w-full",
        "text-sm",
        disabled && "pointer-events-none cursor-not-allowed opacity-50",
        color === "destructive"
          ? "border-destructive focus-within:border-destructive"
          : "border-muted-foreground/25 hover:border-muted-foreground/80 focus-within:border-foreground/60 dark:focus-within:border-white/70",
        className
      )}
    >
      {prefix && (
        <span
          className="flex items-center text-muted-foreground text-[13px] p-2 md:p-2 pr-0"
          onClick={onAffixClick}
        >
          {prefix}
        </span>
      )}

      <input
        type={type}
        inputMode={inputMode}
        className={cn(
          "flex-grow border-0 p-2 md:p-2 min-w-0 outline-0 placeholder:text-muted-foreground text-[13px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
          align === "start" && "text-start",
          align === "end" && "text-end"
        )}
        disabled={disabled}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        value={value}
        ref={inputRef}
      />

      {suffix && (
        <span className="flex items-center text-muted-foreground text-[13px] p-2 md:p-2 pl-0" onClick={onAffixClick}>
          {suffix}
        </span>
      )}
    </div>
  );
};
